import { Injectable, inject, signal } from '@angular/core';
import type Keycloak from 'keycloak-js';
import { AppInfo } from './api.models';
import { ConfigService } from './config.service';

/** Marge (secondes) avant expiration en deçà de laquelle le jeton d'accès est renouvelé. */
const MIN_VALIDITY = 30;

/** Utilisateur Keycloak connecté (claims du jeton d'identité). */
export interface AuthAccount {
  username: string | null;
  name: string | null;
}

/**
 * Authentification Keycloak (client public, flux « authorization code + PKCE » par redirection).
 * La connexion passe directement par le fournisseur Microsoft du realm (kc_idp_hint) quand l'API l'annonce.
 * En mode Development (annoncé par l'API), aucune authentification : l'API traite la requête comme un administrateur.
 * keycloak-js n'est chargé (import dynamique) qu'en mode Keycloak.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly config = inject(ConfigService);
  private keycloak: Keycloak | null = null;
  private idpHint: string | undefined;
  private readonly _account = signal<AuthAccount | null>(null);

  readonly account = this._account.asReadonly();

  get isDevelopment(): boolean {
    return this.config.info()?.auth.mode === 'Development';
  }

  get isAuthenticated(): boolean {
    return this.isDevelopment || this._account() !== null;
  }

  /** Initialise keycloak-js et traite le retour de redirection de connexion. */
  async init(info: AppInfo): Promise<void> {
    if (info.auth.mode === 'Development' || !info.auth.clientId || !info.auth.authority) {
      return;
    }
    // PKCE S256 exige crypto.subtle, que le navigateur ne fournit qu'en HTTPS ou sur localhost / *.localhost.
    if (!globalThis.isSecureContext || !globalThis.crypto?.subtle) {
      throw new Error(
        `Connexion Keycloak impossible sur ${location.origin} : PKCE S256 exige HTTPS ` +
          '(ou localhost / *.localhost). Servir le front en HTTPS (reverse proxy TLS).',
      );
    }
    this.idpHint = info.auth.idpHint ?? undefined;
    const { default: Keycloak } = await import('keycloak-js');
    this.keycloak = new Keycloak({
      clientId: info.auth.clientId,
      oidcProvider: oidcEndpoints(info.auth.authority, location.origin),
    });
    await this.keycloak.init({
      pkceMethod: 'S256',
      // Pas d'iframe de suivi de session (cookies tiers souvent bloqués, CSP frame-src restreinte).
      checkLoginIframe: false,
    });
    this.refreshAccount();
    this.keycloak.onAuthLogout = () => this._account.set(null);
  }

  /** Redirige vers la connexion (Microsoft via Keycloak), puis revient sur l'URL demandée. */
  async login(returnUrl = '/'): Promise<void> {
    // Retour uniquement vers une page de l'application (« /… », jamais « //autre-site »).
    const path = /^\/(?!\/)/.test(returnUrl) ? returnUrl : '/';
    await this.keycloak?.login({
      redirectUri: window.location.origin + path,
      idpHint: this.idpHint,
    });
  }

  async logout(): Promise<void> {
    await this.keycloak?.logout({ redirectUri: window.location.origin + '/' });
  }

  /** Jeton d'accès pour l'API (null en mode Development). */
  async getAccessToken(): Promise<string | null> {
    if (!this.keycloak) {
      return null;
    }
    if (!this.keycloak.authenticated) {
      await this.login(location.pathname + location.search);
      return null;
    }
    try {
      await this.keycloak.updateToken(MIN_VALIDITY);
    } catch {
      // Session Keycloak expirée ou révoquée : reconnexion.
      this._account.set(null);
      await this.login(location.pathname + location.search);
      return null;
    }
    return this.keycloak.token ?? null;
  }

  private refreshAccount(): void {
    const claims = this.keycloak?.authenticated ? this.keycloak.idTokenParsed : undefined;
    this._account.set(
      claims
        ? {
            username: (claims['preferred_username'] as string | undefined) ?? null,
            name: (claims['name'] as string | undefined) ?? null,
          }
        : null,
    );
  }
}

/** Découpe l'URL d'un realm (« https://sso/realms/cotral ») en URL du serveur et nom du realm. */
export function realmOf(authority: string): { url: string; realm: string } {
  const match = /^(.+)\/realms\/([^/]+)\/?$/.exec(authority);
  if (!match) {
    throw new Error(`URL de realm Keycloak invalide : ${authority}`);
  }
  return { url: match[1], realm: decodeURIComponent(match[2]) };
}

/** Chemin, sur l'origine du front, du point de terminaison des jetons relayé par nginx vers Keycloak. */
export const TOKEN_PROXY_PATH = '/oidc/token';

/**
 * Points de terminaison OIDC du realm. Connexion et déconnexion sont des navigations vers Keycloak ; l'échange
 * du code et le renouvellement du jeton (fetch avec credentials: 'include') passent par le relais de même origine
 * (nginx, proxy.conf.json en développement) : le serveur Keycloak ne renvoie pas les en-têtes CORS nécessaires.
 */
export function oidcEndpoints(authority: string, origin: string) {
  const { url, realm } = realmOf(authority);
  const base = `${url}/realms/${encodeURIComponent(realm)}/protocol/openid-connect`;
  return {
    authorization_endpoint: `${base}/auth`,
    token_endpoint: origin + TOKEN_PROXY_PATH,
    end_session_endpoint: `${base}/logout`,
  };
}
